import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import useControllableState from 'helpers/hooks/useControllable';

interface UseQuantityInputOptions {
  value?: number;
  defaultValue?: number;
  minValue?: number;
  maxValue?: number;
  disabled?: boolean;
  onChange?: (val: number) => void;
}

interface UseQuantityInputReturn {
  readonly increment: () => void;
  readonly decrement: () => void;
  readonly canIncrement: boolean;
  readonly canDecrement: boolean;
  readonly inputProps: {
    readonly value: string;
    readonly disabled: boolean;
    readonly onFocus: () => void;
    readonly onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    readonly onBlur: () => void;
    readonly onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  };
}

export function useQuantityInput({
  value: valueProp,
  defaultValue = 0,
  minValue = 0,
  maxValue = Infinity,
  disabled = false,
  onChange,
}: UseQuantityInputOptions): UseQuantityInputReturn {
  const [value, setValue] = useControllableState(valueProp, defaultValue);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const pendingValueRef = useRef<number | null>(null);

  const displayValue = pendingValueRef.current ?? value;

  // Keep a ref to displayValue for use in callbacks without stale closure issues
  const displayValueRef = useRef(displayValue);
  displayValueRef.current = displayValue;

  // Sync editValue with value when not editing, and clear pending when parent catches up
  useEffect(() => {
    if (!isEditing) {
      setEditValue(String(value));
    }
    if (pendingValueRef.current === value) {
      pendingValueRef.current = null;
    }
  }, [value, isEditing]);

  const clamp = useCallback(
    (val: number) => Math.max(minValue, Math.min(maxValue, val)),
    [minValue, maxValue],
  );

  const commit = useCallback(
    (val: number) => {
      if (disabled) return;
      const clamped = clamp(val);
      pendingValueRef.current = clamped;
      setValue(clamped);
      onChange?.(clamped);
    },
    [clamp, setValue, onChange, disabled],
  );

  // Commit any pending edit and return the resolved value
  const commitEditAndGetValue = useCallback(() => {
    if (isEditing) {
      const parsed = parseInt(editValue, 10);
      const committed = isNaN(parsed) ? (pendingValueRef.current ?? value) : clamp(parsed);
      pendingValueRef.current = committed;
      setIsEditing(false);
      return committed;
    }
    return pendingValueRef.current ?? value;
  }, [isEditing, editValue, value, clamp]);

  const increment = useCallback(() => {
    commit(commitEditAndGetValue() + 1);
  }, [commit, commitEditAndGetValue]);

  const decrement = useCallback(() => {
    commit(commitEditAndGetValue() - 1);
  }, [commit, commitEditAndGetValue]);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setEditValue(String(displayValueRef.current));
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const input = e.target.value;

      // Ensure editing mode is active (handles case where input stays focused after button click)
      if (!isEditing) {
        setIsEditing(true);
      }

      // Allow empty input while typing
      if (input === '') {
        setEditValue('');
        return;
      }

      // Only allow positive numeric input (product quantities can't be negative)
      const parsed = parseInt(input, 10);
      if (!isNaN(parsed) && parsed >= 0) {
        setEditValue(input);
      }
    },
    [disabled, isEditing],
  );

  const handleBlur = useCallback(() => {
    setIsEditing(false);

    if (disabled) return;

    const parsed = parseInt(editValue, 10);
    const finalValue = isNaN(parsed) ? displayValueRef.current : clamp(parsed);

    if (finalValue !== displayValueRef.current) {
      pendingValueRef.current = finalValue;
      setValue(finalValue);
      onChange?.(finalValue);
    }
  }, [disabled, editValue, clamp, setValue, onChange]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  }, []);

  return {
    increment,
    decrement,
    canIncrement: displayValue < maxValue && !disabled,
    canDecrement: displayValue > minValue && !disabled,
    inputProps: {
      value: isEditing ? editValue : String(displayValue),
      disabled,
      onFocus: handleFocus,
      onChange: handleChange,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
    },
  };
}
